import { useQuery } from '@apollo/client';
import { DocumentIcon } from '@heroicons/react/24/outline';
import { gql } from '__generated__/gql';
import { InvestorRightEnumType } from '__generated__/graphql';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';

import Badge from 'primitives/Badge';
import LoadingIndicator from 'primitives/LoadingIndicator';

import DetailsPanel from 'components/DetailsPanel';
import ErrorMessage from 'components/ErrorMessage';

import companyEventTypeToReadable from 'utils/enums/company-event-type-to-readable';
import investorRightToReadable from 'utils/enums/investor-right-to-readable';

import CompanyEventSlideOver from './CompanyEventSlideOver';
import CreateCompanyEventDialogButton from './CreateEventDialogButton';

const COMPANY_EVENTS_QUERY = gql(`#graphql
  query fetchEventsByCompany($companyId: ID!) {
    company(id: $companyId) {
      id
      events {
        id
        label
        type
        eventDate
        isAuthorizationRequired
        authorizationStatus
        description
        notes
        investorRightsAdded
        investorRightsRemoved
        createdAt
        documents {
          id
          url
          name
          type
        }
      }
    }
  }
`);

const CompanyEvents: FC = () => {
  const [eventOnDisplay, setEventOnDisplay] = useState('');
  const { companyId } = useParams<{ companyId: string }>() as { companyId: string };

  const { loading, error, data, refetch } = useQuery(COMPANY_EVENTS_QUERY, {
    variables: {
      companyId,
    },
  });

  if (loading) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const events = data.company.events;

  return (
    <>
      <CompanyEventSlideOver
        open={!!eventOnDisplay}
        companyEventId={eventOnDisplay}
        onClose={() => setEventOnDisplay('')}
      />
      <div className="flex justify-between items-center mt-4">
        <h3 className="font-bold text-lg">Events</h3>
        <CreateCompanyEventDialogButton companyId={companyId} />
      </div>
      {events.length === 0 && <p className="text-base text-gray-500 my-4">No events found.</p>}
      <ul className="space-y-6 mb-16">
        {events.map((event, eventIdx) => (
          <li
            key={event.id}
            className="relative flex gap-x-2 cursor-pointer"
            onClick={() => setEventOnDisplay(event.id)}
          >
            <div
              className={classNames(
                eventIdx === events.length - 1 ? 'h-1.5' : '-bottom-6',
                'absolute left-0 top-0 flex w-6 justify-center'
              )}
            >
              <div className="w-px bg-indigo-200" />
            </div>
            <div className="relative flex h-1.5 w-6 flex-none items-center justify-center bg-white">
              <div className="mt-3.5 h-1.5 w-1.5 rounded-full bg-indigo-400 ring-1 ring-indigo-500" />
            </div>
            <div className="w-[720px]">
              <p className="text-sm text-gray-500 mb-2">
                {dayjs(event.eventDate).format('DD MMMM YYYY')}
              </p>
              <DetailsPanel
                displayHorizontally
                title={event.label}
                data={[
                  {
                    label: 'Type',
                    type: 'CUSTOM',
                    value: companyEventTypeToReadable(event.type).label,
                    customValue: () => <Badge {...companyEventTypeToReadable(event.type)} />,
                  },
                  {
                    label: 'Date',
                    value: event.eventDate,
                  },
                  {
                    label: 'Description',
                    value: event.description,
                  },
                  {
                    label: 'Notes',
                    value: event.notes,
                  },
                  {
                    label: 'Changes in Investor Rights',
                    value: event.investorRightsAdded || event.investorRightsRemoved,
                    type: 'CUSTOM',
                    customValue: () => (
                      <div className="flex flex-col gap-y-2 w-full">
                        {event.investorRightsAdded?.length &&
                          event.investorRightsAdded?.length > 0 && (
                            <div className="mb-2">
                              <p className="text-sm font-medium mb-1">Added Rights:</p>
                              <div className="flex flex-col gap-2">
                                {event.investorRightsAdded.map((right, index) => (
                                  <div key={`added-${index}`} className="mr-4">
                                    <Badge
                                      {...investorRightToReadable(
                                        right || InvestorRightEnumType.None
                                      )}
                                      size="sm"
                                      color="green"
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        {event.investorRightsRemoved?.length &&
                          event.investorRightsRemoved?.length > 0 && (
                            <div>
                              <p className="text-sm font-medium mb-1">Removed Rights:</p>
                              <div className="flex flex-col gap-2">
                                {event.investorRightsRemoved.map((right, index) => (
                                  <div key={`removed-${index}`} className="mr-4">
                                    <Badge
                                      {...investorRightToReadable(
                                        right || InvestorRightEnumType.None
                                      )}
                                      size="sm"
                                      color="red"
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                      </div>
                    ),
                  },
                  {
                    label: 'Authorization Required',
                    value: event.isAuthorizationRequired,
                    type: 'BOOLEAN',
                  },
                  {
                    label: 'Documents',
                    value: event.documents,
                    type: 'CUSTOM',
                    customValue: () => (
                      <div>
                        {event.documents && event.documents.length > 0 && (
                          <div className="flex flex-wrap gap-4">
                            {event.documents.map(doc => (
                              <a
                                key={doc!.id}
                                href={doc!.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800"
                                onClick={e => e.stopPropagation()}
                              >
                                <DocumentIcon className="h-4 w-4" />
                                {doc!.name}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          </li>
        ))}
      </ul>
    </>
  );
};

export default CompanyEvents;
